import React from 'react';
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Ticket,
  User,
} from 'lucide-react';
import type { FloatingDockItem } from '../ui/floating-dock';
import type { WebDesignedCartContextValue } from '../../contexts/WebDesignedCartContext';
import type { Language } from '../../contexts/languageContextDef';
import type { User as AppUser } from '../../types';
import { clearCart } from '../../services/cartService';
import { portalHomePathForRole } from '../../lib/portalRouting';
import { dockScrollPage } from './webDesignedDockScroll';

const iconClass = 'text-[#3d2817] dark:text-neutral-100';

function dockAccountInitials(u: Pick<AppUser, 'firstName' | 'lastName' | 'email'>): string {
  const f = u.firstName?.trim();
  const l = u.lastName?.trim();
  if (f && l) return `${f[0]!}${l[0]!}`.toUpperCase();
  if (f) return (f.length >= 2 ? f.slice(0, 2) : `${f}${f[0] ?? ''}`).slice(0, 2).toUpperCase();
  const local = u.email?.split('@')[0] ?? '?';
  return local.slice(0, 2).toUpperCase();
}

function dockAccountGreetingName(u: Pick<AppUser, 'firstName' | 'email'>): string {
  const f = u.firstName?.trim();
  if (f) return f;
  return u.email?.split('@')[0] ?? '';
}

/**
 * Webdesigned floating dock: calendar, cart, account (+ optional scroll / VUM / language).
 * Quick book is never in the dock — only on product cards.
 */
export function buildWebDesignedDockItems(params: {
  pathname: string;
  t: (key: string) => string;
  cart: WebDesignedCartContextValue | null;
  navigate: (opts: { to: string }) => void;
  isSignedIn: boolean;
  role: AppUser['role'];
  includeVumInDock: boolean;
  includeLanguageInDock: boolean;
  /** Scroll up/down — desktop only. */
  includeScrollInDock: boolean;
  /** @deprecated Quick book must stay on product cards; ignored when false. */
  includeBookInDock: boolean;
  language: Language;
  cycleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  /** `<744px` shell only — FR/EN/ZH picker in dock; pad/desktop dock behaviour unchanged. */
  useMobileLanguagePicker: boolean;
  /** `<744px` shell only — signed-in account shows initials + portal/logout menu; pad/desktop unchanged. */
  mobileSignedInAccountDock: boolean;
  appUser: AppUser | null;
  logout: () => void | Promise<void>;
}): FloatingDockItem[] {
  const {
    pathname,
    t,
    cart,
    navigate,
    isSignedIn,
    role,
    includeVumInDock,
    includeLanguageInDock,
    includeScrollInDock,
    includeBookInDock,
    language,
    cycleLanguage,
    setLanguage,
    useMobileLanguagePicker,
    mobileSignedInAccountDock,
    appUser,
    logout,
  } = params;

  /** Assistant: open → re-click closes; if minimized, first click expands. */
  const toggleAssistantFromDock = () => {
    if (!cart) return;
    if (!cart.assistantOpen) {
      cart.openBookingAssistant();
      return;
    }
    if (cart.assistantMinimized) {
      cart.setAssistantMinimized(false);
      return;
    }
    cart.setAssistantOpen(false);
  };

  const vumLabel = cart?.vumPromo
    ? cart.vumPromo.code.length > 10
      ? `${cart.vumPromo.code.slice(0, 8)}…`
      : cart.vumPromo.code
    : t('common.promoCodes') || 'VUM';

  const vumItem: FloatingDockItem = {
    title: vumLabel,
    description: cart?.vumPromo ? 'Code VUM' : t('common.promoCodes') || 'Code promo VUM',
    onClick: () => {
      if (!cart) return;
      cart.setVumPromoModalOpen(!cart.vumPromoModalOpen);
    },
    icon: <Ticket className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
  };

  const initials = appUser ? dockAccountInitials(appUser) : '';
  const greetingName = appUser ? dockAccountGreetingName(appUser) : '';
  const portalPath = portalHomePathForRole(role);

  const accountItem: FloatingDockItem =
    isSignedIn && mobileSignedInAccountDock && appUser
      ? {
          mobileSignedInAccount: true,
          title: `${t('common.hello') || 'Hello'}, ${greetingName}`,
          description: t('common.accountDockSignedInHint'),
          initials,
          portalLabel: t('common.clientPortal') || t('common.myAccount') || 'Client portal',
          logoutLabel: t('common.logout') || 'Logout',
          onOpenPortal: () => navigate({ to: portalPath }),
          onLogout: async () => {
            clearCart();
            await logout();
          },
          icon: (
            <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold leading-none text-[#3d2817]">
              {initials}
            </span>
          ),
        }
      : isSignedIn
        ? {
            title: t('common.clientPortal') || t('common.myAccount') || 'Client portal',
            onClick: () => navigate({ to: portalPath }),
            icon: <User className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
          }
        : {
            title: t('clerkAuth.signInTitle') || 'Login',
            description: 'Sign in',
            onClick: () => {
              if (!cart) return;
              if (cart.authPanelOpen) {
                cart.setAuthPanelOpen(false);
                return;
              }
              if (pathname !== '/' && pathname !== '/webdesigned' && pathname !== '/webdesigned/') {
                navigate({ to: pathname });
              }
              cart.openAuthPanel('signin');
            },
            icon: <User className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
          };

  const scrollUpTitle = t('common.scrollUp') || 'Scroll up';
  const scrollDownTitle = t('common.scrollDown') || 'Scroll down';

  const languageAbbr = language === 'fr' ? 'FR' : language === 'en' ? 'EN' : 'ZH';

  const languageItem: FloatingDockItem =
    includeLanguageInDock && useMobileLanguagePicker
      ? {
          title: `${t('common.language') || 'Language'} · ${languageAbbr}`,
          description: t('common.languageDockSelect') || 'Choose language',
          languagePicker: true,
          currentLanguage: language,
          onSelectLanguage: setLanguage,
          languageOptions: [
            { code: 'fr', label: 'FR' },
            { code: 'en', label: 'EN' },
            { code: 'zh', label: 'ZH' },
          ],
          icon: (
            <span className={`text-[11px] font-bold leading-none tracking-wide ${iconClass}`}>
              {languageAbbr}
            </span>
          ),
        }
      : {
          title: `${t('common.language') || 'Language'} · ${languageAbbr}`,
          description: t('common.languageDockCycle') || 'Tap to switch language',
          onClick: cycleLanguage,
          icon: (
            <span className={`text-[11px] font-bold leading-none tracking-wide ${iconClass}`}>
              {languageAbbr}
            </span>
          ),
        };

  const core: FloatingDockItem[] = [];

  if (includeScrollInDock) {
    core.push(
      {
        title: scrollUpTitle,
        onClick: () => dockScrollPage(pathname, 'up'),
        icon: <ChevronUp className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
      },
      {
        title: scrollDownTitle,
        onClick: () => dockScrollPage(pathname, 'down'),
        icon: <ChevronDown className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
      }
    );
  }

  core.push(
    {
      dockKey: 'calendar',
      title: t('common.calendar') || 'Calendar assistant',
      description: t('common.calendarAssistantToggle') || 'Open or close assistant',
      onClick: toggleAssistantFromDock,
      icon: <CalendarDays className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
    },
    {
      title: t('common.cart') || 'Cart',
      description: t('common.cartToggle') || 'Open or close cart',
      onClick: () => {
        if (!cart) return;
        cart.setCartSidebarOpen(!cart.cartSidebarOpen);
      },
      icon: <ShoppingCart className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />,
    }
  );

  if (includeVumInDock) {
    core.push(vumItem);
  }

  // Quick book stays on product cards only — never surface a Book tile in the dock.
  void includeBookInDock;

  if (includeLanguageInDock) {
    core.push(languageItem);
  }

  core.push(accountItem);

  return core;
}
